import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { fmt } from "@/hooks/useRates";
import { useLang } from "@/i18n";

function drawCard(rates, usdinr) {
  const W = 1080;
  const rows = rates.length;
  const H = 560 + rows * 88;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");

  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#25D366";
  ctx.lineWidth = 6;
  ctx.strokeRect(24, 24, W - 48, H - 48);

  ctx.fillStyle = "#25D366";
  ctx.fillRect(72, 80, 84, 84);
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "900 40px Arial";
  ctx.fillText("MM", 84, 136);

  ctx.fillStyle = "#FDFDFD";
  ctx.font = "900 58px Arial";
  ctx.fillText("MAHIMA METALS", 184, 138);
  ctx.fillStyle = "#25D366";
  ctx.font = "600 26px Arial";
  const date = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  ctx.fillText(`Today's Rates · ${date}`, 184, 178);

  if (usdinr) {
    ctx.fillStyle = "#FDFDFD";
    ctx.font = "700 28px Arial";
    ctx.fillText(`1 USD = ₹${fmt(usdinr, 2)}`, 72, 250);
  }

  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(72, 280);
  ctx.lineTo(W - 72, 280);
  ctx.stroke();

  let y = 350;
  rates.forEach((r) => {
    const feat = r.symbol === "COPPER" || r.symbol === "ALUMINIUM";
    if (feat) {
      ctx.fillStyle = "rgba(212,175,55,0.10)";
      ctx.fillRect(56, y - 44, W - 112, 72);
    }
    ctx.fillStyle = feat ? "#25D366" : "#FDFDFD";
    ctx.font = "800 32px Arial";
    ctx.fillText(r.symbol, 72, y);
    ctx.fillStyle = "#FDFDFD";
    ctx.font = "700 34px Arial";
    ctx.textAlign = "right";
    ctx.fillText(`₹${fmt(r.mcx, r.dec)}`, 660, y);
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "600 28px Arial";
    ctx.fillText(r.lme != null ? `$${fmt(r.lme, r.lme_dec)}` : "—", 900, y);
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "400 18px Arial";
    ctx.fillText(r.mcx_unit, W - 84, y);
    ctx.textAlign = "left";
    y += 88;
  });

  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.beginPath();
  ctx.moveTo(72, y - 30);
  ctx.lineTo(W - 72, y - 30);
  ctx.stroke();

  ctx.fillStyle = "#25D366";
  ctx.font = "800 34px Arial";
  ctx.fillText("Call: +91 80000 93073", 72, y + 40);
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "500 24px Arial";
  ctx.fillText("Instagram: @mahima_metal · Rates indicative", 72, y + 84);
  return c;
}

function ratesText(rates, usdinr) {
  const date = new Date().toLocaleDateString("en-IN");
  let t = `*MAHIMA METALS — Today's Rates (${date})*\n`;
  if (usdinr) t += `1 USD = ₹${fmt(usdinr, 2)}\n`;
  t += "\n";
  rates.forEach((r) => {
    t += `${r.symbol}: ₹${fmt(r.mcx, r.dec)}${r.lme != null ? ` / $${fmt(r.lme, r.lme_dec)}` : ""} (${r.mcx_unit})\n`;
  });
  t += "\nCall: +91 80000 93073";
  return t;
}

export default function ShareRatesButton({ rates, usdinr }) {
  const { str } = useLang();

  const share = async () => {
    if (!rates.length) return;
    const canvas = drawCard(rates, usdinr);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    const file = new File([blob], "mahima-metals-rates.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: ratesText(rates, usdinr) });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mahima-metals-rates.png";
    a.click();
    URL.revokeObjectURL(url);
    window.open(`https://wa.me/?text=${encodeURIComponent(ratesText(rates, usdinr))}`, "_blank");
    toast.success("Rate card downloaded — attach it in WhatsApp!");
  };

  return (
    <button
      onClick={share}
      data-testid="share-rates-btn"
      className="inline-flex items-center gap-2 border border-[#25D366]/60 px-5 py-2.5 font-ticker text-[11px] tracking-[0.2em] text-[#25D366] uppercase hover:bg-[#25D366] hover:text-[#0A0A0A] transition-colors duration-300"
    >
      <Share2 size={13} /> {str.shareRates}
    </button>
  );
}
